import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { formatRands, shortId, ORDER_STATUSES, statusLabel } from "@/lib/order";
import { useRoom } from "@/hooks/use-room";
import { OrderMap } from "@/components/common/OrderMap";
import { StatusBadge, StatusTimeline } from "@/components/common/StatusViews";
import { CenterSpinner, ErrorState } from "@/components/common/States";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export default function OrderTracking() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [driverLoc, setDriverLoc] = useState(null);

  const { data: order, isLoading, isError, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: () => api(`/orders/${id}`),
  });

  useRoom(id ? `order:${id}` : null, (e) => {
    if (e.event === "DRIVER_LOCATION" && e.location) setDriverLoc(e.location);
    if (["ORDER_STATUS", "DRIVER_ASSIGNED"].includes(e.event)) {
      qc.invalidateQueries({ queryKey: ["order", id] });
    }
  });

  if (isLoading) return <CenterSpinner />;
  if (isError || !order) return <ErrorState onRetry={refetch} message="Unable to load this order." />;

  const historyMap = {};
  (order.status_history || []).forEach((h) => (historyMap[h.status] = h.at));

  const points = [
    { lat: order.vendor_lat, lng: order.vendor_lng, type: "vendor", label: order.vendor_name },
    { lat: order.delivery_lat, lng: order.delivery_lng, type: "customer", label: "Delivery" },
  ];
  const dl = driverLoc || order.driver_location;
  if (dl?.lat != null) points.push({ lat: dl.lat, lng: dl.lng, type: "driver", label: order.driver_name || "Driver" });

  const canCancel = order.status === "PENDING";

  const cancel = async () => {
    try {
      await api(`/orders/${id}/status`, { method: "PATCH", json: { status: "CANCELLED" } });
      toast.success("Order cancelled");
      qc.invalidateQueries({ queryKey: ["order", id] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-2xl">{order.vendor_name}</h1>
          <StatusBadge status={order.status} />
        </div>
        <p className="text-sm text-muted-foreground">
          {shortId(order.order_id)} · ETA ~{order.eta_minutes} min
        </p>

        <OrderMap points={points} height={340} />

        {order.driver_name && (
          <Card className="rounded-2xl p-4">
            <p className="text-sm text-muted-foreground">Your driver</p>
            <p className="font-display text-base">{order.driver_name}</p>
            {order.driver_phone && <p className="text-sm text-muted-foreground">{order.driver_phone}</p>}
          </Card>
        )}

        {order.status === "DELIVERED" && <ReviewBlock order={order} onDone={() => qc.invalidateQueries({ queryKey: ["order", id] })} />}
      </div>

      <div className="space-y-4">
        <Card className="rounded-2xl p-5">
          <h2 className="mb-4 font-display text-lg">Progress</h2>
          {["CANCELLED", "REFUNDED"].includes(order.status) ? (
            <p className="text-sm text-muted-foreground">This order is {statusLabel(order.status).toLowerCase()}.</p>
          ) : (
            <StatusTimeline steps={ORDER_STATUSES} current={order.status} history={historyMap} />
          )}
          {canCancel && (
            <Button variant="outline" className="mt-5 w-full rounded-full text-destructive" onClick={cancel} data-testid="cancel-order-button">
              Cancel order
            </Button>
          )}
        </Card>

        <Card className="rounded-2xl p-5">
          <h2 className="mb-3 font-display text-lg">Items</h2>
          <div className="space-y-2 text-sm">
            {order.items?.map((it) => (
              <div key={it.product_id} className="flex justify-between">
                <span>{it.quantity}× {it.name}</span>
                <span className="text-muted-foreground">{formatRands(it.unit_price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-between border-t pt-3 font-display">
            <span>Total</span>
            <span>{formatRands(order.total)}</span>
          </div>
        </Card>

        <Link to="/orders" className="block text-center text-sm text-muted-foreground hover:text-foreground">
          ← All orders
        </Link>
      </div>
    </div>
  );
}

function ReviewBlock({ order, onDone }) {
  const [rating, setRating] = useState(order.review?.rating || 0);
  const [comment, setComment] = useState(order.review?.comment || "");
  const done = !!order.review;

  const submit = async () => {
    try {
      await api("/reviews", { method: "POST", json: { order_id: order.order_id, rating, comment } });
      toast.success("Thanks for your review!");
      onDone();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <Card className="rounded-2xl p-5" data-testid="review-block">
      <h2 className="font-display text-lg">{done ? "Your review" : "Rate your order"}</h2>
      <div className="mt-3 flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} disabled={done} onClick={() => setRating(n)} data-testid={`star-${n}`}>
            <Star className={cn("h-7 w-7", n <= rating ? "fill-[hsl(var(--warning))] text-[hsl(var(--warning))]" : "text-muted-foreground")} />
          </button>
        ))}
      </div>
      {!done && (
        <>
          <Textarea className="mt-3" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Tell others about your experience…" data-testid="review-comment" />
          <Button className="mt-3 rounded-full" disabled={!rating} onClick={submit} data-testid="submit-review-button">
            Submit review
          </Button>
        </>
      )}
      {done && comment && <p className="mt-2 text-sm text-muted-foreground">{comment}</p>}
    </Card>
  );
}
